import React, { useEffect, useState, useCallback } from 'react';
import { API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  IndianRupee, TrendingUp, TrendingDown, Users, Building2, 
  CreditCard, AlertTriangle, Calendar, RefreshCw, ArrowUpRight,
  ArrowDownRight, Clock, FileText, PieChart, BarChart3
} from "lucide-react";

const AdminFinanceDashboard = () => {
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [transactions, setTransactions] = useState(null);
  const [schoolFinancials, setSchoolFinancials] = useState(null);
  const [forecasting, setForecasting] = useState(null);
  const [revenueChart, setRevenueChart] = useState(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, subsRes, transRes, schoolsRes, forecastRes, chartRes] = await Promise.all([
        fetch(`${API}/admin/finance/overview?period=${period}`, { credentials: 'include' }),
        fetch(`${API}/admin/finance/subscriptions`, { credentials: 'include' }),
        fetch(`${API}/admin/finance/transactions?limit=10`, { credentials: 'include' }),
        fetch(`${API}/admin/finance/school-financials`, { credentials: 'include' }),
        fetch(`${API}/admin/finance/forecasting`, { credentials: 'include' }),
        fetch(`${API}/admin/finance/revenue-chart?period=${period}`, { credentials: 'include' })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (subsRes.ok) setSubscriptions(await subsRes.json());
      if (transRes.ok) setTransactions(await transRes.json());
      if (schoolsRes.ok) setSchoolFinancials(await schoolsRes.json());
      if (forecastRes.ok) setForecasting(await forecastRes.json());
      if (chartRes.ok) setRevenueChart(await chartRes.json());
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value}%`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const metrics = overview?.metrics || {};
  const subMetrics = subscriptions?.metrics || {};

  return (
    <div className="space-y-6" data-testid="admin-finance-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
            Financial Dashboard
          </h1>
          <p className="text-slate-600 mt-1">
            Revenue analytics and subscription metrics
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-36" data-testid="period-selector">
              <SelectValue placeholder="Select period" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchAllData} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-slate-200" data-testid="metric-mrr">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">MRR</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(metrics.mrr)}</p>
                <p className="text-xs text-slate-500 mt-1">Monthly Recurring Revenue</p>
              </div>
              <div className="bg-emerald-50 p-3 rounded-xl">
                <IndianRupee className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-arr">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">ARR</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(metrics.arr)}</p>
                <p className="text-xs text-slate-500 mt-1">Annual Recurring Revenue</p>
              </div>
              <div className="bg-blue-50 p-3 rounded-xl">
                <TrendingUp className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-revenue">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Total Revenue</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{formatCurrency(metrics.total_revenue)}</p>
                <div className="flex items-center gap-1 mt-1">
                  {metrics.growth_rate >= 0 ? (
                    <ArrowUpRight className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <ArrowDownRight className="h-4 w-4 text-red-500" />
                  )}
                  <span className={`text-xs font-medium ${metrics.growth_rate >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                    {formatPercent(metrics.growth_rate)} vs prev period
                  </span>
                </div>
              </div>
              <div className="bg-purple-50 p-3 rounded-xl">
                <BarChart3 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="metric-transactions">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Transactions</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{metrics.transaction_count || 0}</p>
                <p className="text-xs text-slate-500 mt-1">Completed in period</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-xl">
                <CreditCard className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different sections */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="subscriptions" data-testid="tab-subscriptions">Subscriptions</TabsTrigger>
          <TabsTrigger value="schools" data-testid="tab-schools">B2B Schools</TabsTrigger>
          <TabsTrigger value="forecasting" data-testid="tab-forecasting">Forecasting</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue by Source */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-blue-600" />
                  Revenue by Source
                </CardTitle>
                <CardDescription>B2C vs B2B breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Users className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">B2C (Direct)</span>
                    </div>
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(metrics.b2c_revenue)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">B2B (Schools)</span>
                    </div>
                    <span className="text-xl font-bold text-purple-600">{formatCurrency(metrics.b2b_revenue)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Revenue by Plan */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-emerald-600" />
                  Revenue by Plan
                </CardTitle>
                <CardDescription>Breakdown by subscription tier</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(metrics.revenue_by_plan || {}).map(([plan, amount]) => (
                    <div key={plan} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="capitalize">{plan}</Badge>
                      </div>
                      <span className="font-semibold">{formatCurrency(amount)}</span>
                    </div>
                  ))}
                  {Object.keys(metrics.revenue_by_plan || {}).length === 0 && (
                    <p className="text-slate-500 text-center py-4">No revenue data for this period</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Transactions */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-slate-600" />
                  Recent Transactions
                </CardTitle>
                <CardDescription>Last 10 transactions</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions?.transactions?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-slate-200">
                          <th className="text-left py-3 px-2 font-medium text-slate-500">Date</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-500">Amount</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-500">Plan</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-500">Status</th>
                          <th className="text-left py-3 px-2 font-medium text-slate-500">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.transactions.map((tx, idx) => (
                          <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="py-3 px-2 text-slate-600">
                              {new Date(tx.created_at).toLocaleDateString('en-IN')}
                            </td>
                            <td className="py-3 px-2 font-medium">{formatCurrency(tx.amount)}</td>
                            <td className="py-3 px-2">
                              <Badge variant="outline" className="capitalize">{tx.plan_type || 'N/A'}</Badge>
                            </td>
                            <td className="py-3 px-2">
                              <Badge 
                                variant={tx.status === 'completed' ? 'default' : 'secondary'}
                                className={tx.status === 'completed' ? 'bg-emerald-100 text-emerald-700' : ''}
                              >
                                {tx.status}
                              </Badge>
                            </td>
                            <td className="py-3 px-2 text-slate-500">{tx.source || 'B2C'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No transactions found</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Subscriptions Tab */}
        <TabsContent value="subscriptions" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total Subscribers</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{subMetrics.total_subscribers || 0}</p>
                  </div>
                  <Users className="h-5 w-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Active Paid</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">{subMetrics.active_paid || 0}</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Active Trials</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">{subMetrics.active_trials || 0}</p>
                  </div>
                  <Badge className="bg-amber-100 text-amber-700">Trial</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Conversion Rate</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">{subMetrics.conversion_rate || 0}%</p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Plan Distribution */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Plan Distribution</CardTitle>
                <CardDescription>Active subscriptions by plan</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(subMetrics.plan_distribution || {}).map(([plan, count]) => {
                    const colors = {
                      free_trial: 'bg-amber-100 text-amber-700',
                      starter: 'bg-blue-100 text-blue-700',
                      plus: 'bg-purple-100 text-purple-700',
                      pro: 'bg-emerald-100 text-emerald-700'
                    };
                    return (
                      <div key={plan} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <Badge className={colors[plan] || 'bg-slate-100 text-slate-700'} variant="secondary">
                          {plan.replace('_', ' ').toUpperCase()}
                        </Badge>
                        <span className="text-xl font-bold">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Key Metrics */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle>Key Metrics</CardTitle>
                <CardDescription>ARPU, LTV, and Churn</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-600">ARPU</p>
                      <p className="text-xs text-slate-500">Average Revenue Per User</p>
                    </div>
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(subMetrics.arpu)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-600">LTV</p>
                      <p className="text-xs text-slate-500">Lifetime Value (Est.)</p>
                    </div>
                    <span className="text-xl font-bold text-emerald-600">{formatCurrency(subMetrics.ltv)}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div>
                      <p className="text-sm text-slate-600">Churn Rate</p>
                      <p className="text-xs text-slate-500">Monthly churn</p>
                    </div>
                    <span className="text-xl font-bold text-red-600">{subMetrics.churn_rate || 0}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* B2B Schools Tab */}
        <TabsContent value="schools" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Total B2B Revenue</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {formatCurrency(schoolFinancials?.total_b2b_revenue)}
                    </p>
                  </div>
                  <Building2 className="h-5 w-5 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Active Schools</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">{schoolFinancials?.school_count || 0}</p>
                  </div>
                  <Badge className="bg-emerald-100 text-emerald-700">Verified</Badge>
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pending Invoices</p>
                    <p className="text-2xl font-bold text-amber-600 mt-1">
                      {schoolFinancials?.invoices?.pending || 0}
                    </p>
                  </div>
                  <Clock className="h-5 w-5 text-amber-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Outstanding</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">
                      {formatCurrency(schoolFinancials?.invoices?.outstanding_amount)}
                    </p>
                  </div>
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Schools by Revenue */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-purple-600" />
                Top Schools by Revenue
              </CardTitle>
              <CardDescription>Highest revenue generating schools</CardDescription>
            </CardHeader>
            <CardContent>
              {schoolFinancials?.schools?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-500">School</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-500">Code</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-500">Students</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-500">Revenue</th>
                        <th className="text-right py-3 px-2 font-medium text-slate-500">Rev/Student</th>
                        <th className="text-left py-3 px-2 font-medium text-slate-500">Plan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {schoolFinancials.schools.map((school, idx) => (
                        <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 font-medium text-slate-800">{school.name}</td>
                          <td className="py-3 px-2 text-slate-500 font-mono text-xs">{school.school_code}</td>
                          <td className="py-3 px-2 text-right">{school.students}</td>
                          <td className="py-3 px-2 text-right font-semibold text-emerald-600">
                            {formatCurrency(school.revenue)}
                          </td>
                          <td className="py-3 px-2 text-right text-slate-600">
                            {formatCurrency(school.revenue_per_student)}
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="capitalize">{school.plan}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No school data available</p>
              )}
            </CardContent>
          </Card>

          {/* Invoice Summary */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle>Invoice Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-2xl font-bold text-slate-800">{schoolFinancials?.invoices?.total || 0}</p>
                  <p className="text-sm text-slate-500">Total Invoices</p>
                </div>
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{schoolFinancials?.invoices?.paid || 0}</p>
                  <p className="text-sm text-slate-500">Paid</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{schoolFinancials?.invoices?.pending || 0}</p>
                  <p className="text-sm text-slate-500">Pending</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{schoolFinancials?.invoices?.overdue || 0}</p>
                  <p className="text-sm text-slate-500">Overdue</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Forecasting Tab */}
        <TabsContent value="forecasting" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Renewals */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Upcoming Renewals (7 days)
                </CardTitle>
                <CardDescription>
                  {forecasting?.upcoming_renewals?.count || 0} renewals expected
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-slate-600">Projected Revenue</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatCurrency(forecasting?.upcoming_renewals?.projected_revenue)}
                    </p>
                  </div>
                  {forecasting?.upcoming_renewals?.list?.length > 0 && (
                    <div className="space-y-2">
                      {forecasting.upcoming_renewals.list.slice(0, 5).map((sub, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm font-medium">{sub.child_name}</span>
                          <span className="text-xs text-slate-500">
                            {new Date(sub.subscription?.end_date).toLocaleDateString('en-IN')}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Trial Expirations */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" />
                  Trial Expirations (7 days)
                </CardTitle>
                <CardDescription>
                  {forecasting?.trial_expirations?.count || 0} trials expiring
                </CardDescription>
              </CardHeader>
              <CardContent>
                {forecasting?.trial_expirations?.list?.length > 0 ? (
                  <div className="space-y-2">
                    {forecasting.trial_expirations.list.map((sub, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium">{sub.child_name}</p>
                          <p className="text-xs text-slate-500">{sub.parent_email}</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">
                          {new Date(sub.subscription?.end_date).toLocaleDateString('en-IN')}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-4">No trials expiring soon</p>
                )}
              </CardContent>
            </Card>

            {/* At-Risk Accounts */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                  At-Risk Accounts
                </CardTitle>
                <CardDescription>
                  Accounts with failed payments or low activity
                </CardDescription>
              </CardHeader>
              <CardContent>
                {forecasting?.at_risk_accounts?.list?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {forecasting.at_risk_accounts.list.map((account, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                        <div>
                          <p className="text-sm font-medium text-slate-800">{account.child_name}</p>
                          <p className="text-xs text-slate-500">{account.parent_email}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="destructive" className="text-xs">At Risk</Badge>
                          {account.last_activity && (
                            <p className="text-xs text-slate-500 mt-1">
                              Last: {new Date(account.last_activity).toLocaleDateString('en-IN')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <TrendingUp className="h-6 w-6 text-emerald-600" />
                    </div>
                    <p className="text-emerald-600 font-medium">No at-risk accounts!</p>
                    <p className="text-slate-500 text-sm">All accounts are healthy</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Expiring Soon Summary */}
            <Card className="border-slate-200 lg:col-span-2">
              <CardHeader>
                <CardTitle>30-Day Forecast Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-xl">
                    <p className="text-3xl font-bold text-blue-600">{forecasting?.expiring_soon || 0}</p>
                    <p className="text-sm text-slate-600 mt-1">Expiring in 30 days</p>
                  </div>
                  <div className="text-center p-4 bg-emerald-50 rounded-xl">
                    <p className="text-3xl font-bold text-emerald-600">
                      {forecasting?.upcoming_renewals?.count || 0}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">Expected Renewals</p>
                  </div>
                  <div className="text-center p-4 bg-amber-50 rounded-xl">
                    <p className="text-3xl font-bold text-amber-600">
                      {forecasting?.trial_expirations?.count || 0}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">Trials Ending</p>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-xl">
                    <p className="text-3xl font-bold text-red-600">
                      {forecasting?.at_risk_accounts?.count || 0}
                    </p>
                    <p className="text-sm text-slate-600 mt-1">At-Risk Accounts</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminFinanceDashboard;
